import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { alertsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import type { Alert } from "@/lib/types";

interface AlertHistoryViewProps {
  tripId: string;
}

export default function AlertHistoryView({ tripId }: AlertHistoryViewProps) {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ["alerts", tripId],
    queryFn: () => alertsApi.getByTrip(tripId),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      alertsApi.update(id, { active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts", tripId] });
      queryClient.invalidateQueries({ queryKey: ["trip", tripId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => alertsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts", tripId] });
      queryClient.invalidateQueries({ queryKey: ["trip", tripId] });
    },
  });

  const alerts: Alert[] = data?.alerts || [];

  if (alerts.length === 0) return null;

  return (
    <div className="space-y-2">
      {alerts.map((alert) => {
        const channels: string[] = Array.isArray(alert.channels)
          ? alert.channels
          : typeof alert.channels === "string"
            ? (() => { try { return JSON.parse(alert.channels as string); } catch { return []; } })()
            : [];

        return (
          <Card key={alert.id}>
            <CardContent className="flex items-center gap-3 py-3 px-4">
              <button
                type="button"
                onClick={() => toggleMutation.mutate({ id: alert.id, active: !alert.active })}
                disabled={toggleMutation.isPending}
                className="shrink-0"
              >
                <Badge
                  className={
                    alert.active
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 cursor-pointer hover:bg-emerald-100"
                      : "bg-red-50 text-red-700 border-red-200 cursor-pointer hover:bg-red-100"
                  }
                >
                  {alert.active ? "Active" : "Inactive"}
                </Badge>
              </button>
              <span className="flex-1 text-sm">
                {alert.type === "price_drop"
                  ? "Price drop alert"
                  : alert.threshold
                    ? `Alert when price < ${alert.threshold}`
                    : "Price alert"}
              </span>
              <div className="flex gap-1.5">
                {channels.map((ch) => (
                  <Badge key={ch} variant="secondary" className="text-xs">
                    {ch}
                  </Badge>
                ))}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive h-8 w-8 p-0"
                onClick={() => deleteMutation.mutate(alert.id)}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
